import React, { useEffect, useState } from "react";
import axios from "axios";
import BuildAlternativeModal from "./BuildAlternativeModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtUsd = (n) => (n == null ? "—" : `$${Math.round(n).toLocaleString("en-US")}`);

// ---------- Mini panels ----------
const Panel = ({ icon, label, accent, headline, sub, children, testid }) => {
  const accents = {
    emerald: { ring: "ring-emerald-200", bg: "bg-emerald-50/60", icon: "text-emerald-600", num: "text-emerald-700" },
    amber:   { ring: "ring-amber-200",   bg: "bg-amber-50/60",   icon: "text-amber-600",   num: "text-amber-700" },
    rose:    { ring: "ring-rose-200",    bg: "bg-rose-50/60",    icon: "text-rose-600",    num: "text-rose-700" },
    violet:  { ring: "ring-violet-200",  bg: "bg-violet-50/60",  icon: "text-violet-600",  num: "text-violet-700" },
    blue:    { ring: "ring-blue-200",    bg: "bg-blue-50/60",    icon: "text-blue-600",    num: "text-blue-700" },
    zinc:    { ring: "ring-zinc-200",    bg: "bg-zinc-50",       icon: "text-zinc-500",    num: "text-zinc-600" },
  };
  const a = accents[accent] || accents.violet;
  return (
    <div className={`rounded-2xl ring-1 ${a.ring} ${a.bg} backdrop-blur p-4 sm:p-5`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-lg leading-none ${a.icon}`}>{icon}</span>
        <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</span>
      </div>
      <div className="flex items-baseline gap-2 mb-1">
        <span className={`text-2xl sm:text-3xl font-bold tabular-nums ${a.num}`}>{headline}</span>
      </div>
      {sub && <div className="text-xs text-zinc-500 mb-3">{sub}</div>}
      {children}
    </div>
  );
};

// ---------- Truth panel ----------
const TruthPanel = ({ truth }) => {
  if (!truth) return null;
  const platforms = truth.platforms || [];
  const total = truth.total_signals || 0;
  const accent = total >= 20 ? "emerald" : total >= 5 ? "violet" : "amber";
  const subText = platforms.length
    ? `${platforms.length} platform${platforms.length > 1 ? "s" : ""} · ${(truth.distinct_subreddits || []).length || 0} distinct communities`
    : "No matched signals";
  return (
    <Panel
      icon="✅"
      label="Real signal"
      accent={accent}
      headline={total}
      sub={subText}
      testid="trust-panel-truth"
    >
      <div className="flex flex-wrap gap-1.5">
        {platforms.map((p) => (
          <span key={p.name} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-white/80 border border-zinc-200 text-zinc-700">
            <span className="font-semibold">{p.count}</span> {p.name}
            {/* Surface posts vs comments split for Reddit — comments are confirmation signal */}
            {p.name === "Reddit" && (p.comment_count || 0) > 0 && (
              <span className="text-zinc-400 text-[10px] ml-0.5">
                ({p.post_count || 0}p · {p.comment_count}c)
              </span>
            )}
          </span>
        ))}
      </div>
      {/* Comment-confirmation line — only render when there's a meaningful comment volume */}
      {(truth.reddit_comment_count || 0) >= 3 && (
        <div className="mt-2 text-[11px] text-zinc-600 leading-snug" data-testid="trust-panel-truth-confirmations">
          <span className="font-semibold text-emerald-700">{truth.reddit_comment_count}</span> Redditors confirmed the problem in replies
          <span className="text-zinc-400"> · {(truth.reddit_post_count || 0)} top posts surfaced it</span>
        </div>
      )}
      {/* Spam-filter transparency — surfaces the cleanup work so users can trust the count */}
      {(truth.spam_filtered || 0) > 0 && (
        <div className="mt-1.5 text-[10.5px] text-zinc-400 italic" data-testid="trust-panel-truth-spam-filter">
          🧹 {truth.spam_filtered} self-promo post{truth.spam_filtered === 1 ? "" : "s"} excluded
        </div>
      )}
      {(truth.distinct_subreddits || []).length > 0 && (
        <div className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
          <span className="font-semibold text-zinc-700">Communities:</span>{" "}
          {(truth.distinct_subreddits || []).slice(0, 6).map((s) => `r/${s}`).join(" · ")}
          {(truth.distinct_subreddits || []).length > 6 ? ", …" : ""}
        </div>
      )}
    </Panel>
  );
};

// ---------- Crowded panel ----------
const CrowdedPanel = ({ crowded }) => {
  if (!crowded) return null;
  const n = crowded.named_competitor_count || 0;
  const top = (crowded.top_competitors || []).slice(0, 5);
  const accent = n === 0 ? "emerald" : n <= 2 ? "violet" : n <= 5 ? "amber" : "rose";
  const headline = n === 0 ? "Clear" : String(n);
  const sub = n === 0
    ? "No named tools mentioned in raw posts"
    : `${n} distinct domain${n > 1 ? "s" : ""} mentioned in your signal pool`;
  return (
    <Panel
      icon={n === 0 ? "🚀" : n <= 5 ? "⚔️" : "⚠️"}
      label="Competition"
      accent={accent}
      headline={headline}
      sub={sub}
      testid="trust-panel-crowded"
    >
      {top.length > 0 ? (
        <ul className="space-y-1.5">
          {top.map((c) => (
            <li key={c.domain} className="flex items-center gap-2 text-[12.5px]">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${n <= 5 ? "bg-amber-400" : "bg-rose-500"}`} />
              <span className="font-mono text-zinc-800">{c.domain}</span>
              <span className="text-zinc-400 text-[11px]">· {c.mentions}× mentions</span>
              {c.first_seen_url && (
                <a
                  href={c.first_seen_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto text-[10px] text-violet-600 hover:text-violet-800 underline decoration-dotted"
                >
                  source ↗
                </a>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-[12px] text-emerald-700 font-medium">
          Clean — no entrenched competitors found in the signal pool. First-mover window appears open.
        </div>
      )}
    </Panel>
  );
};

// ---------- Budgets panel ----------
const BudgetsPanel = ({ budgets }) => {
  if (!budgets) return null;
  const n = budgets.real_listings || 0;
  const med = budgets.median_usd;
  const accent = med >= 50 ? "emerald" : med >= 20 ? "violet" : n > 0 ? "amber" : "zinc";
  const examples = (budgets.examples || []).slice(0, 3);
  return (
    <Panel
      icon="💰"
      label="Real budgets paid"
      accent={accent}
      headline={med ? fmtUsd(med) : "—"}
      sub={n > 0
        ? `Median across ${n} real Fiverr/Upwork/Freelancer listing${n > 1 ? "s" : ""} · p10 ${fmtUsd(budgets.p10_usd)} · p90 ${fmtUsd(budgets.p90_usd)}`
        : "No paid listings matched"}
      testid="trust-panel-budgets"
    >
      {examples.length > 0 ? (
        <ul className="space-y-1.5">
          {examples.map((ex, i) => (
            <li key={`${ex.url}-${i}`} className="text-[12.5px] flex items-start gap-2">
              <span className="text-emerald-600 font-mono font-semibold whitespace-nowrap">
                {fmtUsd(ex.price_min)}–{fmtUsd(ex.price_max)}
              </span>
              <span className="text-zinc-600 flex-1 min-w-0 truncate" title={ex.title}>
                {ex.title || "(untitled)"}
              </span>
              {ex.url && (
                <a
                  href={ex.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-violet-600 hover:text-violet-800 underline decoration-dotted whitespace-nowrap"
                >
                  ↗
                </a>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-[12px] text-zinc-500 italic">
          Strong demand signal but no explicit paid-gig budgets to anchor pricing.
        </div>
      )}
    </Panel>
  );
};

// ---------- WTP quotes panel ----------
const WtpPanel = ({ wtp, topPosts }) => {
  if (!wtp) return null;
  const n = wtp.mention_count || 0;
  const med = wtp.median_usd;
  // Fallback: when zero WTP quotes, surface top post snippets instead so the
  // user always has *some* verbatim evidence to read.
  const accent = n >= 3 ? "emerald" : n >= 1 ? "violet" : "blue";
  const useTopPosts = n === 0 && (topPosts || []).length > 0;
  return (
    <Panel
      icon={useTopPosts ? "🗣️" : "💬"}
      label={useTopPosts ? "Top complaints (verbatim)" : "Willingness to pay"}
      accent={accent}
      headline={useTopPosts ? (topPosts || []).length : (med ? fmtUsd(med) : n)}
      sub={useTopPosts
        ? `Top ${(topPosts || []).length} posts by upvotes — actual words your users use`
        : (n > 0
          ? `${n} explicit "I'd pay $X" quote${n > 1 ? "s" : ""} from your signal pool`
          : "No explicit pricing quotes — see top complaints below"
        )}
      testid="trust-panel-wtp"
    >
      {useTopPosts ? (
        <ul className="space-y-2">
          {(topPosts || []).slice(0, 3).map((p, i) => (
            <li key={`${p.url}-${i}`} className="text-[12px] border-l-2 border-violet-200 pl-2.5">
              <div className="text-zinc-800 line-clamp-2 leading-snug font-medium">{p.title}</div>
              <div className="mt-1 text-[10px] text-zinc-400 flex items-center gap-2">
                <span>r/{p.subreddit} · ↑{p.score}</span>
                {p.url && (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-600 hover:text-violet-800 underline decoration-dotted ml-auto"
                  >
                    read full post ↗
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <>
          {/* Mini histogram — buckets of WTP amounts (only when ≥3 quotes, otherwise noisy) */}
          {n >= 3 && wtp.histogram && wtp.histogram_max > 0 && (
            <div className="mb-3" data-testid="trust-panel-wtp-histogram">
              <div className="flex items-end gap-1.5 h-14">
                {wtp.histogram.map((b) => {
                  const pct = wtp.histogram_max > 0 ? (b.count / wtp.histogram_max) * 100 : 0;
                  const isPeak = b.count === wtp.histogram_max;
                  return (
                    <div key={b.label} className="flex-1 flex flex-col items-center justify-end gap-0.5 min-w-0">
                      <div className="text-[9px] tabular-nums text-zinc-500 leading-none">{b.count || ""}</div>
                      <div
                        className={`w-full rounded-t transition-all ${isPeak ? "bg-emerald-500" : "bg-emerald-200"}`}
                        style={{ height: `${Math.max(pct * 0.4, b.count > 0 ? 6 : 2)}px` }}
                        title={`${b.count} quote${b.count !== 1 ? "s" : ""} in ${b.label}`}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex items-end gap-1.5 mt-1">
                {wtp.histogram.map((b) => (
                  <div key={b.label} className="flex-1 text-[9px] text-zinc-500 text-center font-mono truncate" title={b.label}>
                    {b.low === 0 ? `<$${b.high}` : b.high >= 10000 ? `$${b.low}+` : `$${b.low}-${b.high}`}
                  </div>
                ))}
              </div>
              {(wtp.min_usd != null && wtp.max_usd != null) && (
                <div className="mt-2 text-[10.5px] text-zinc-500 tabular-nums">
                  Range: <span className="text-zinc-800 font-semibold">${wtp.min_usd}</span>
                  {" – "}<span className="text-zinc-800 font-semibold">${wtp.max_usd}</span>
                  {wtp.p25_usd != null && wtp.p75_usd != null && (
                    <span className="text-zinc-400"> · middle 50%: ${wtp.p25_usd}–${wtp.p75_usd}</span>
                  )}
                </div>
              )}
            </div>
          )}
          <ul className="space-y-2">
            {(wtp.quotes || []).slice(0, 3).map((q, i) => (
              <li key={i} className="text-[12px] border-l-2 border-emerald-200 pl-2.5">
                <div className="text-zinc-800 line-clamp-3 leading-snug italic">"…{q.quote}…"</div>
                <div className="mt-1 text-[10px] text-zinc-400 flex items-center gap-2">
                  <span className="text-emerald-700 font-mono font-bold">${q.amount_usd}</span>
                  <span>· r/{q.subreddit}</span>
                  {q.source && q.source !== "verb" && (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-zinc-100 text-zinc-500 uppercase tracking-wider" title={`Matched pattern: ${q.source}`}>
                      {q.source}
                    </span>
                  )}
                  {q.post_url && (
                    <a
                      href={q.post_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-violet-600 hover:text-violet-800 underline decoration-dotted ml-auto"
                    >
                      source ↗
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </Panel>
  );
};

// ---------- TAM panel ----------
const TamPanel = ({ tam }) => {
  if (!tam) return null;
  if (!tam.sufficient_data) {
    return (
      <Panel icon="🧮" label="Market size" accent="zinc" headline="—" sub="Not enough matched signals yet — re-check after next scan" testid="trust-panel-tam">
        <div className="text-[12px] text-zinc-500 italic">
          Need at least 5 matched signals to estimate market size with any honesty.
        </div>
      </Panel>
    );
  }
  const fmtRange = (lo, hi) => `${fmtUsd(lo)}–${fmtUsd(hi)}`;
  const headline = fmtRange(tam.mrr_low_usd, tam.mrr_high_usd);
  return (
    <Panel
      icon="🧮"
      label="Market size estimate"
      accent={tam.tier_color}
      headline={headline}
      sub={`per-month MRR potential · ${tam.tier}`}
      testid="trust-panel-tam"
    >
      {/* Math waterfall — every multiplier is shown so the founder can audit */}
      <ul className="space-y-1 text-[11.5px] sm:text-[12px] text-zinc-700 font-mono">
        <li className="flex items-baseline justify-between gap-2">
          <span className="text-zinc-500">visible pained users</span>
          <span className="tabular-nums font-semibold text-zinc-900">{tam.visible_pained_users}</span>
        </li>
        <li className="flex items-baseline justify-between gap-2" title="Most pained users never post — published research suggests only 1-3% do">
          <span className="text-zinc-500">× visibility bias</span>
          <span className="tabular-nums">{tam.visibility_low}–{tam.visibility_high}×</span>
        </li>
        <li className="flex items-baseline justify-between gap-2 pt-1 border-t border-zinc-200">
          <span className="text-zinc-500">addressable buyers/mo</span>
          <span className="tabular-nums font-semibold">{tam.addressable_low.toLocaleString()}–{tam.addressable_high.toLocaleString()}</span>
        </li>
        <li className="flex items-baseline justify-between gap-2" title="B2B SaaS landing page conversion benchmark">
          <span className="text-zinc-500">× conversion</span>
          <span className="tabular-nums">{tam.conversion_rate_pct}%</span>
        </li>
        <li className="flex items-baseline justify-between gap-2 pt-1 border-t border-zinc-200">
          <span className="text-zinc-500">paying customers/mo</span>
          <span className="tabular-nums font-semibold">{tam.paying_customers_low}–{tam.paying_customers_high}</span>
        </li>
        <li className="flex items-baseline justify-between gap-2" title={tam.acv_is_default ? "No real budgets matched — using $20 default" : "From your real budgets panel above"}>
          <span className="text-zinc-500">× ACV {tam.acv_is_default && <span className="text-amber-600">(default)</span>}</span>
          <span className="tabular-nums">{fmtUsd(tam.acv_usd)}</span>
        </li>
        <li className="flex items-baseline justify-between gap-2 pt-1 border-t-2 border-zinc-300">
          <span className="text-zinc-700 font-semibold">= MRR potential</span>
          <span className="tabular-nums font-bold text-zinc-900">{headline}</span>
        </li>
        <li className="flex items-baseline justify-between gap-2 text-[10.5px]">
          <span className="text-zinc-400">Year 1 revenue range</span>
          <span className="tabular-nums text-zinc-500">{fmtRange(tam.year_one_low_usd, tam.year_one_high_usd)}</span>
        </li>
      </ul>
      <div className={`mt-3 px-2.5 py-2 rounded-lg text-[11.5px] leading-snug ${tam.tier_color === "emerald" ? "bg-emerald-100/70 text-emerald-900" : tam.tier_color === "violet" ? "bg-violet-100/70 text-violet-900" : tam.tier_color === "amber" ? "bg-amber-100/70 text-amber-900" : "bg-zinc-100 text-zinc-700"}`}>
        <span className="font-semibold">Verdict: </span>{tam.tier_note}
      </div>
    </Panel>
  );
};

// ---------- Score-trend sparkline panel ----------
const Sparkline = ({ points, color = "#8B5CF6", height = 36 }) => {
  if (!points || points.length === 0) return null;
  const w = Math.max(120, points.length * 18);
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = Math.max(1, max - min);
  const pad = 4;
  const path = points
    .map((p, i) => {
      const x = (i / Math.max(1, points.length - 1)) * (w - pad * 2) + pad;
      const y = height - pad - ((p - min) / range) * (height - pad * 2);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  const area = `${path} L ${w - pad} ${height - pad} L ${pad} ${height - pad} Z`;
  const last = points[points.length - 1];
  const lastX = w - pad;
  const lastY = height - pad - ((last - min) / range) * (height - pad * 2);
  return (
    <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" className="w-full" style={{ height }}>
      <defs>
        <linearGradient id="sparkFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#sparkFill)" />
      <path d={path} stroke={color} strokeWidth="1.75" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r="3" fill={color} />
      <circle cx={lastX} cy={lastY} r="6" fill={color} opacity="0.25" />
    </svg>
  );
};

const ScoreTrendPanel = ({ opportunityId }) => {
  const [hist, setHist] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/opportunities/${opportunityId}/history?days=90`, { timeout: 10000 })
      .then((r) => { if (!cancelled) setHist(r.data); })
      .catch((e) => { if (!cancelled) setErr(e.message); });
    return () => { cancelled = true; };
  }, [opportunityId]);

  if (err) return null;
  if (!hist) {
    return (
      <Panel icon="📈" label="Score over time" accent="zinc" headline="…" sub="Loading history…" testid="trust-panel-trend">
        <div className="h-9 bg-zinc-100 animate-pulse rounded" />
      </Panel>
    );
  }
  const n = hist.snapshot_count;
  if (n < 2) {
    return (
      <Panel
        icon="📈"
        label="Score over time"
        accent="zinc"
        headline={Math.round(hist.current_score || 0)}
        sub="Building 14-day history — check back tomorrow"
        testid="trust-panel-trend"
      >
        <div className="text-[12px] text-zinc-500 italic">
          Today is snapshot #1. Daily cron writes a new data point every 23:30 UTC — sparkline appears once we have 2+ days.
        </div>
      </Panel>
    );
  }
  const points = hist.snapshots.map((s) => Number(s.score));
  const accent = hist.direction === "rising" ? "emerald" : hist.direction === "falling" ? "rose" : "blue";
  const color = accent === "emerald" ? "#10B981" : accent === "rose" ? "#F43F5E" : "#3B82F6";
  const arrow = hist.direction === "rising" ? "↗" : hist.direction === "falling" ? "↘" : "→";
  const deltaTxt = hist.delta !== null
    ? `${hist.delta > 0 ? "+" : ""}${hist.delta} pts${hist.pct_change !== null ? ` (${hist.pct_change > 0 ? "+" : ""}${hist.pct_change}%)` : ""}`
    : "—";
  const verdictNote =
    hist.direction === "rising"
      ? "Momentum is building — consider acting sooner rather than later."
      : hist.direction === "falling"
      ? "Chatter is cooling. This window may be closing — validate quickly or move on."
      : "Score is stable. No urgency signal either way.";
  return (
    <Panel
      icon="📈"
      label="Score over time"
      accent={accent}
      headline={`${arrow} ${deltaTxt}`}
      sub={`${n} snapshot${n > 1 ? "s" : ""} · ${hist.first_score?.toFixed(0)} → ${hist.current_score?.toFixed(0)} over ${hist.days_requested}d`}
      testid="trust-panel-trend"
    >
      <Sparkline points={points} color={color} />
      <div className={`mt-2 px-2.5 py-2 rounded-lg text-[11.5px] leading-snug ${accent === "emerald" ? "bg-emerald-100/70 text-emerald-900" : accent === "rose" ? "bg-rose-100/70 text-rose-900" : "bg-blue-100/70 text-blue-900"}`}>
        {verdictNote}
      </div>
    </Panel>
  );
};

// ---------- Wedge panel (your gap vs. incumbents — deterministic complaint themes) ----------
const _WEDGE_ANGLE_META = {
  "support quality":  { icon: "🎧", color: "amber"   },
  "pricing":          { icon: "💸", color: "rose"    },
  "speed":            { icon: "⚡",  color: "blue"    },
  "reliability":      { icon: "🛠",  color: "orange"  },
  "feature gaps":     { icon: "🧩", color: "violet"  },
  "ui / ux":          { icon: "🎨", color: "fuchsia" },
  "documentation":    { icon: "📚", color: "slate"   },
  "integrations":     { icon: "🔌", color: "indigo"  },
  "complexity":       { icon: "🧱", color: "stone"   },
  "data export":      { icon: "📦", color: "zinc"    },
  "manual / tedious": { icon: "🥱", color: "amber"   },
};
const _WEDGE_TONE = {
  amber:   "bg-amber-50  ring-amber-200  text-amber-800",
  rose:    "bg-rose-50   ring-rose-200   text-rose-800",
  blue:    "bg-blue-50   ring-blue-200   text-blue-800",
  orange:  "bg-orange-50 ring-orange-200 text-orange-800",
  violet:  "bg-violet-50 ring-violet-200 text-violet-800",
  fuchsia: "bg-fuchsia-50 ring-fuchsia-200 text-fuchsia-800",
  slate:   "bg-slate-50  ring-slate-200  text-slate-800",
  indigo:  "bg-indigo-50 ring-indigo-200 text-indigo-800",
  stone:   "bg-stone-50  ring-stone-200  text-stone-800",
  zinc:    "bg-zinc-50   ring-zinc-200   text-zinc-800",
};

const WedgePanel = ({ wedge }) => {
  const [openAngle, setOpenAngle] = useState(0);
  const [briefSlug, setBriefSlug] = useState(null);  // {slug, productName} | null
  if (!wedge) return null;
  const angles = wedge.wedge_angles || [];
  const incumbents = wedge.incumbent_wedges || [];

  // Pretty-format incumbent identifiers. Reddit-derived ones are bare domains
  // ("intercom.com") which we keep as-is. G2-derived ones are product slugs
  // ("quickbooks-online" → "QuickBooks Online"). Known acronyms get fixed casing.
  const _INCUMBENT_ACRONYMS = { qb: "QB", quickbooks: "QuickBooks", freshbooks: "FreshBooks", honeybook: "HoneyBook", simplepractice: "SimplePractice", mycase: "MyCase", housecall: "Housecall", clio: "Clio", shopmonkey: "Shopmonkey", jobber: "Jobber", chatgpt: "ChatGPT", servicetitan: "ServiceTitan", procore: "Procore", mindbody: "Mindbody", cozi: "Cozi" };
  const prettyIncumbent = (raw) => {
    if (!raw) return raw;
    // If it looks like a domain (contains a dot), keep as-is.
    if (raw.includes(".")) return raw;
    return raw.split("-").map(w => _INCUMBENT_ACRONYMS[w.toLowerCase()] || (w.charAt(0).toUpperCase() + w.slice(1))).join(" ");
  };
  // An incumbent is "G2-attributed" iff any of its top complaints came from G2.
  // Only those incumbents get the Build-alt button (the endpoint requires a G2 slug).
  const isG2Incumbent = (inc) => (inc.top_complaints || []).some(c => c.source === "g2");

  if (angles.length === 0 && incumbents.length === 0) {
    return (
      <div className="sm:col-span-2 rounded-2xl ring-1 ring-zinc-200 bg-zinc-50/60 p-4 sm:p-5" data-testid="trust-panel-wedge-empty">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg leading-none text-zinc-500">🎯</span>
          <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">Your wedge</span>
        </div>
        <div className="text-[12px] text-zinc-500 italic">
          No competitor complaints captured in current signals. Run a deeper scan or check back as more posts roll in — wedge angles appear when users start naming what they hate.
        </div>
      </div>
    );
  }

  return (
    <div
      className="sm:col-span-2 rounded-2xl ring-1 ring-rose-200/80 bg-gradient-to-br from-rose-50/70 via-white to-amber-50/30 p-4 sm:p-5"
      data-testid="trust-panel-wedge"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg leading-none text-rose-600">🎯</span>
          <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
            Your wedge — what users in this space hate
          </span>
        </div>
        <span className="text-[10px] text-zinc-400 italic">{wedge.total_complaints} complaint{wedge.total_complaints === 1 ? "" : "s"} · deterministic, no AI</span>
      </div>

      <p className="mb-4 text-[12.5px] text-zinc-600 leading-snug">
        These are the most-upvoted complaint themes pulled from raw posts. Build the version that doesn't suck at <span className="font-semibold text-zinc-800">{angles[0]?.label || "—"}</span>{angles[1] ? <> or <span className="font-semibold text-zinc-800">{angles[1].label}</span></> : ""} and you've already got a positioning angle.
      </p>

      {/* Theme tabs */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="trust-panel-wedge-tabs">
        {angles.map((a, i) => {
          const meta = _WEDGE_ANGLE_META[a.label] || { icon: "•", color: "zinc" };
          const tone = _WEDGE_TONE[meta.color];
          const active = i === openAngle;
          return (
            <button
              key={a.label}
              type="button"
              onClick={() => setOpenAngle(i)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1 transition ${
                active ? `${tone} ring-2` : "bg-white ring-zinc-200 text-zinc-600 hover:bg-zinc-50"
              }`}
              data-testid={`trust-panel-wedge-tab-${a.label.replace(/[^a-z0-9]/gi, "-")}`}
            >
              <span>{meta.icon}</span>
              <span className="capitalize">{a.label}</span>
              <span className="text-[10px] tabular-nums opacity-70">· {a.mentions}</span>
            </button>
          );
        })}
      </div>

      {/* Evidence for the active angle */}
      {angles[openAngle] && (
        <div className="rounded-xl bg-white/80 ring-1 ring-zinc-200 p-3 mb-3" data-testid="trust-panel-wedge-evidence">
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">
            Top {angles[openAngle].top_evidence.length} quote{angles[openAngle].top_evidence.length === 1 ? "" : "s"} · "{angles[openAngle].label}"
          </div>
          <ul className="space-y-2">
            {angles[openAngle].top_evidence.map((e, i) => (
              <li key={i} className="text-[12px] border-l-2 border-rose-200 pl-2.5">
                <div className="text-zinc-800 line-clamp-3 leading-snug italic">"…{e.quote}…"</div>
                <div className="mt-1 text-[10px] text-zinc-400 flex items-center gap-2 flex-wrap">
                  <span className="text-rose-700 font-mono font-bold tabular-nums">▲ {e.upvotes}</span>
                  {e.subreddit && <span>· r/{e.subreddit}</span>}
                  {e.post_url && (
                    <a
                      href={e.post_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-violet-600 hover:text-violet-800 underline decoration-dotted ml-auto"
                    >
                      source ↗
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Per-incumbent wedges — only render when present (rare but high-value) */}
      {incumbents.length > 0 && (
        <div className="pt-3 border-t border-zinc-200/70" data-testid="trust-panel-wedge-incumbents">
          <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">
            Direct complaints about named competitors
          </div>
          <div className="space-y-2">
            {incumbents.map((inc) => (
              <div key={inc.incumbent} className="rounded-lg bg-white/70 ring-1 ring-zinc-200 p-2.5">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className="text-[11px] font-bold text-zinc-900">{prettyIncumbent(inc.incumbent)}</span>
                  <span className="text-[10px] text-zinc-500">· {inc.complaint_count} complaint{inc.complaint_count === 1 ? "" : "s"}</span>
                  {isG2Incumbent(inc) && (
                    <button
                      type="button"
                      onClick={() => setBriefSlug({ slug: inc.incumbent, productName: prettyIncumbent(inc.incumbent) })}
                      className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-violet-600 text-white hover:bg-violet-700 transition-colors"
                      data-testid={`wedge-build-alt-${inc.incumbent}`}
                      title={`Draft a Claude-powered MVP brief targeting ${prettyIncumbent(inc.incumbent)}`}
                    >
                      ⚡ Build alt
                    </button>
                  )}
                </div>
                <ul className="space-y-1">
                  {inc.top_complaints.slice(0, 2).map((c, i) => (
                    <li key={i} className="text-[11.5px] text-zinc-700 italic leading-snug">
                      <span className="text-rose-600 font-semibold non-italic mr-1">·</span>
                      "…{c.quote.slice(0, 180)}…"
                      {c.theme && (
                        <span className="ml-1.5 text-[9.5px] not-italic px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 uppercase tracking-wide">
                          {c.theme}
                        </span>
                      )}
                      {c.post_url && (
                        <a href={c.post_url} target="_blank" rel="noopener noreferrer" className="ml-1.5 text-[10px] text-violet-600 hover:text-violet-800 underline decoration-dotted not-italic">↗</a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Build-alternative modal (Claude-drafted MVP brief targeted at a G2 incumbent) */}
      {briefSlug && (
        <BuildAlternativeModal
          slug={briefSlug.slug}
          productName={briefSlug.productName}
          onClose={() => setBriefSlug(null)}
        />
      )}
    </div>
  );
};

// ---------- First 10 Customers panel (full-width, expandable) ----------
const OUTREACH_CYCLE = {
  null: "messaged",
  none: "messaged",
  messaged: "replied",
  replied: "customer",
  customer: null,
};
const OUTREACH_META = {
  messaged: { label: "Messaged", icon: "✉", classes: "bg-blue-600 text-white ring-blue-700" },
  replied:  { label: "Replied",  icon: "💬", classes: "bg-amber-500 text-white ring-amber-600" },
  customer: { label: "Customer", icon: "🎉", classes: "bg-emerald-600 text-white ring-emerald-700" },
};

const First10CustomersPanel = ({ customers, opportunityId }) => {
  const [open, setOpen] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  // Map of post_url → "messaged" | "replied" | "customer"
  const [statuses, setStatuses] = useState({});
  // post_url that's currently mid-flight (disables the pill briefly)
  const [pending, setPending] = useState(null);
  // Whether the user is logged in & tracker is usable.
  const [trackerEnabled, setTrackerEnabled] = useState(true);

  // Fetch initial outreach statuses for this opportunity
  useEffect(() => {
    if (!opportunityId) return;
    const token = typeof window !== "undefined" ? localStorage.getItem("p2p_token") : null;
    if (!token) { setTrackerEnabled(false); return; }
    let cancelled = false;
    axios
      .get(`${API}/me/outreach`, {
        params: { opportunity_id: opportunityId },
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
      })
      .then((r) => { if (!cancelled) setStatuses(r.data?.statuses || {}); })
      .catch(() => { if (!cancelled) setTrackerEnabled(false); });
    return () => { cancelled = true; };
  }, [opportunityId]);

  if (!customers) return null;
  const posts = customers.posts || [];
  const n = posts.length;

  if (n === 0) {
    return (
      <div className="sm:col-span-2 rounded-2xl ring-1 ring-zinc-200 bg-zinc-50/60 p-4 sm:p-5" data-testid="trust-panel-first10-empty">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg leading-none text-zinc-500">👥</span>
          <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">Your first 10 customers</span>
        </div>
        <div className="text-[12px] text-zinc-500 italic">
          No Reddit posts matched yet — once more signals come in, real usernames will show up here with ready-to-send DMs.
        </div>
      </div>
    );
  }

  const copy = async (text, idx) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1600);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopiedIdx(idx); setTimeout(() => setCopiedIdx(null), 1600); } catch (_) {}
      document.body.removeChild(ta);
    }
  };

  const cyclePill = async (postUrl) => {
    if (!trackerEnabled || !postUrl || pending === postUrl) return;
    const current = statuses[postUrl] || null;
    const next = OUTREACH_CYCLE[current ?? "none"];
    const token = typeof window !== "undefined" ? localStorage.getItem("p2p_token") : null;
    if (!token) { setTrackerEnabled(false); return; }
    setPending(postUrl);
    // Optimistic update
    const prevStatuses = statuses;
    const optimistic = { ...statuses };
    if (next) optimistic[postUrl] = next;
    else delete optimistic[postUrl];
    setStatuses(optimistic);
    try {
      await axios.post(
        `${API}/me/outreach`,
        { opportunity_id: opportunityId, post_url: postUrl, status: next },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 },
      );
    } catch (e) {
      // Rollback on failure
      setStatuses(prevStatuses);
    } finally {
      setPending(null);
    }
  };

  // Counter rollup
  const counts = { messaged: 0, replied: 0, customer: 0 };
  Object.values(statuses).forEach((s) => { if (counts[s] !== undefined) counts[s] += 1; });
  const reached = counts.messaged + counts.replied + counts.customer;
  const pctReached = Math.min(100, Math.round((reached / Math.max(n, 1)) * 100));

  const visible = open ? posts : posts.slice(0, 3);

  return (
    <div
      className="sm:col-span-2 rounded-2xl ring-1 ring-violet-200 bg-gradient-to-br from-violet-50/70 via-white to-blue-50/40 p-4 sm:p-5"
      data-testid="trust-panel-first10"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg leading-none text-violet-600">👥</span>
          <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
            Your first {n} customers
          </span>
        </div>
        <span className="text-[10px] text-zinc-400 italic">Real Redditors · pre-drafted DMs · deterministic, no AI</span>
      </div>

      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-2xl sm:text-3xl font-bold tabular-nums text-violet-700">{n}</span>
        <span className="text-xs text-zinc-500">people who already complained about this exact problem</span>
      </div>
      <div className="text-[11.5px] text-zinc-500 mb-3">
        These are the highest-upvoted real posts in your signal pool. Click a username's post, then send the pre-drafted DM. Aim for 10 conversations — that's your validation pre-launch.
      </div>

      {/* Conversation tracker counter strip */}
      {trackerEnabled && (
        <div className="mb-4 rounded-xl bg-white/70 ring-1 ring-zinc-200 p-3" data-testid="trust-panel-first10-counter">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <div className="flex items-center gap-2 text-[11px] font-semibold text-zinc-700">
              <span className="tabular-nums text-zinc-900">{reached}/{n}</span>
              <span className="text-zinc-500 font-normal">reached</span>
              <span className="text-zinc-300">·</span>
              <span className="inline-flex items-center gap-1 text-blue-700"><span>✉</span>{counts.messaged}</span>
              <span className="text-zinc-300">·</span>
              <span className="inline-flex items-center gap-1 text-amber-700"><span>💬</span>{counts.replied}</span>
              <span className="text-zinc-300">·</span>
              <span className="inline-flex items-center gap-1 text-emerald-700"><span>🎉</span>{counts.customer}</span>
            </div>
            <span className="text-[10px] text-zinc-400 italic">
              {reached === 0 ? "Tap a pill below to start tracking conversations" :
               counts.customer >= 1 ? "First customer landed — you've validated." :
               counts.replied >= 1 ? "You've got a reply — keep pushing." :
               "Track progress as you message each lead."}
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-zinc-100 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 via-amber-400 to-emerald-500 transition-all duration-500"
              style={{ width: `${pctReached}%` }}
              data-testid="trust-panel-first10-progress"
            />
          </div>
        </div>
      )}

      <ul className="space-y-3" data-testid="trust-panel-first10-list">
        {visible.map((p, i) => {
          const upvotes = Number(p.score || 0);
          const isCopied = copiedIdx === i;
          const postUrl = p.url || "";
          const status = statuses[postUrl] || null;
          const meta = status ? OUTREACH_META[status] : null;
          const isPending = pending === postUrl;
          return (
            <li key={i} className="rounded-xl bg-white/80 ring-1 ring-zinc-200 p-3 sm:p-3.5" data-testid={`trust-panel-first10-item-${i}`}>
              <div className="flex items-start justify-between gap-2 sm:gap-3 mb-1.5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-x-1.5 gap-y-1 text-[10.5px] text-zinc-500 mb-0.5 flex-wrap">
                    <span className="font-mono font-bold text-violet-700">#{i + 1}</span>
                    <span className="text-zinc-300">·</span>
                    <span className="font-semibold text-zinc-700 truncate max-w-[110px] sm:max-w-none">r/{p.subreddit || "unknown"}</span>
                    <span className="text-zinc-300">·</span>
                    <span className="tabular-nums">▲ {upvotes.toLocaleString()}</span>
                    {p.user_type && (
                      <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 text-[9.5px] uppercase tracking-wide">{p.user_type}</span>
                    )}
                    {trackerEnabled && postUrl && (
                      <button
                        type="button"
                        onClick={() => cyclePill(postUrl)}
                        disabled={isPending}
                        title="Click to cycle: Messaged → Replied → Customer → none"
                        className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 transition ${
                          meta
                            ? `${meta.classes} hover:opacity-90`
                            : "bg-white text-zinc-500 ring-zinc-300 hover:bg-zinc-50 hover:text-zinc-700"
                        } ${isPending ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
                        data-testid={`trust-panel-first10-pill-${i}`}
                        data-status={status || "none"}
                      >
                        {meta ? <><span>{meta.icon}</span><span>{meta.label}</span></> : "+ Track"}
                      </button>
                    )}
                  </div>
                  <div className="text-[12.5px] font-semibold text-zinc-900 line-clamp-2 leading-snug">
                    {p.title || "(no title)"}
                  </div>
                  {p.snippet && (
                    <div className="mt-1 text-[11.5px] text-zinc-600 italic line-clamp-2 leading-snug">
                      "{p.snippet}"
                    </div>
                  )}
                </div>
                {p.reddit_message_url && (
                  <a
                    href={p.reddit_message_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-[11px] px-2.5 py-1 rounded-md bg-violet-600 hover:bg-violet-700 text-white font-semibold whitespace-nowrap transition self-start"
                    data-testid={`trust-panel-first10-open-${i}`}
                  >
                    Open ↗
                  </a>
                )}
              </div>

              <div className="mt-2 rounded-lg bg-zinc-50 ring-1 ring-zinc-200 p-2.5">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-[9.5px] font-bold uppercase tracking-wider text-zinc-500">Suggested DM</span>
                  <button
                    type="button"
                    onClick={() => copy(p.suggested_dm || "", i)}
                    className={`text-[11px] px-2 py-0.5 rounded font-semibold transition ${isCopied ? "bg-emerald-600 text-white" : "bg-white ring-1 ring-zinc-300 text-zinc-700 hover:bg-zinc-100"}`}
                    data-testid={`trust-panel-first10-copy-${i}`}
                  >
                    {isCopied ? "✓ Copied" : "Copy"}
                  </button>
                </div>
                <p className="text-[11.5px] text-zinc-700 leading-snug whitespace-pre-wrap">
                  {p.suggested_dm}
                </p>
              </div>
            </li>
          );
        })}
      </ul>

      {n > 3 && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-3 text-[11.5px] font-semibold text-violet-700 hover:text-violet-900 underline decoration-dotted"
          data-testid="trust-panel-first10-toggle"
        >
          {open ? `Show top 3 only ↑` : `Show all ${n} customers ↓`}
        </button>
      )}
    </div>
  );
};

// ---------- Main TrustCard ----------
export default function TrustCard({ opportunityId, compact = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/opportunities/${opportunityId}/evidence`, { timeout: 12000 })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [opportunityId]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white/60 p-5 text-sm text-zinc-500 animate-pulse" data-testid="trust-card-loading">
        Counting real signals…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-5 text-sm text-rose-700" data-testid="trust-card-error">
        Could not load evidence: {error}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="rounded-3xl border border-zinc-200 bg-gradient-to-br from-white via-violet-50/20 to-blue-50/20 p-4 sm:p-6 shadow-[0_24px_60px_-32px_rgba(94,115,193,0.25)]" data-testid="trust-card">
      <div className="flex items-center justify-between mb-4 sm:mb-5 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] text-violet-600">
            ★ Counted from real data — every number is clickable
          </div>
          <h3 className="font-display text-lg sm:text-xl font-bold text-zinc-900 mt-0.5 leading-tight">
            Trust check — should you build this?
          </h3>
        </div>
        {data.matched_keywords?.length > 0 && (
          <div className="text-[10px] text-zinc-400 max-w-full">
            <span className="font-semibold text-zinc-500">Matched on:</span>{" "}
            <span className="font-mono">{data.matched_keywords.slice(0, 5).join(" · ")}</span>
          </div>
        )}
      </div>

      <div className={`grid gap-3 sm:gap-4 ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-2"}`}>
        <TruthPanel truth={data.truth} />
        <CrowdedPanel crowded={data.crowded} />
        <WedgePanel wedge={data.wedge} />
        <BudgetsPanel budgets={data.budgets} />
        <WtpPanel wtp={data.willingness_to_pay} topPosts={data.top_posts} />
        <ScoreTrendPanel opportunityId={opportunityId} />
        <TamPanel tam={data.tam} />
        <First10CustomersPanel customers={data.first_10_customers} opportunityId={opportunityId} />
      </div>

      <div className="mt-4 sm:mt-5 text-[11px] text-zinc-400 italic flex items-center gap-2">
        <span className="text-violet-500">ⓘ</span>
        Zero AI hallucination — every count and quote comes from a real scraped post. Click any link to verify.
      </div>
    </div>
  );
}
